<template>
  <div class="case-review-view">
    <section class="section dynamic-height" ref="form">
      <div class="columns is-multiline">
        <div class="column is-12">
          <div class="sticky-top" :class="hasShadow">
            <title-component
              v-if="kase.id"
              :size="1">
              <translate>Review case</translate>
              {{ kase.caseNumber }}
            </title-component>
          </div>
          <br>
          <div class="scrollable-content">
            <grid-details-component
              v-if="kase.id"
              :display_spec="caseSpecs"
              :item="kase"
              :item_keys="case_keys">
            </grid-details-component>
            <!-- Show Work proof review if the case is of type reporting -->
            <title-component
              v-if="reportingReviewResults.length"
              :size="2"
              :lineSeparator="'under'">
              <translate>Work proof</translate>
            </title-component>
            <review-customer-grid-component
              v-if="reportingReviewResults.length"
              :reviewResults="reportingReviewResults"
              :columnClasses="'is-12-mobile is-12-tablet is-12-desktop'"
              v-on:showFinding="toggleFinding($event)">
            </review-customer-grid-component>
            <!-- Show Customer review results if the case is of type entry -->
            <review-service-component
              v-if="customerReviewResults.length"
              :title="'Customer'">
              <review-customer-grid-component
                v-if="customerReviewResults.length"
                :reviewResults="customerReviewResults"
                :columnClasses="'is-12-mobile is-6-tablet is-6-desktop'"
                v-on:showFinding="toggleFinding($event)">
              </review-customer-grid-component>
            </review-service-component>
            <!-- Show Certification list (case is of type: entry)-->
            <title-component
              v-if="serviceReviewResults.length"
              :size="2"
              :lineSeparator="'under'">
              <translate>Certification</translate>
            </title-component>
            <review-customer-documents-component
              v-if="serviceDocumentsReviewResults.length"
              :reviewResults="serviceDocumentsReviewResults"
              v-on:showFinding="toggleFinding($event)">
            </review-customer-documents-component>
            <br>
            <review-customer-exam-component
              v-if="serviceRadioReviewResults.length"
              :reviewResults="serviceRadioReviewResults"
              :columnClasses="'is-12-mobile is-12-tablet is-12-desktop'"
              v-on:showFinding="toggleFinding($event)">
            </review-customer-exam-component>
          </div>
        </div>
      </div>
    </section>
    <section class="section dynamic-height">
      <div class="columns is-multilined">
        <div class="column is-12">
          <tabs-router-component class="route-tabs" :tabs="routes" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
  import { caseSpecs } from '@/display_context/display_context'
  import TitleComponent from '@/components/layout/TitleComponent'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent'
  import ReviewCustomerGridComponent from '@/components/layout/custom_grid/ReviewCustomerGridComponent'
  import ReviewServiceComponent from '@/components/layout/ReviewServiceComponent'
  import ReviewCustomerDocumentsComponent from '@/components/layout/ReviewCustomerDocumentsComponent'
  import ReviewCustomerExamComponent from '@/components/layout/ReviewCustomerExamComponent'
  import { ReviewResult } from '@/classes/case/ReviewResult'
  import { itemRefresher, setPageHeightMixin } from '@/mixins/mixins'
  import TabsRouterComponent from '@/components/layout/tabs/TabsRouterComponent.vue'
  import { globalEventBus } from '@/utility/utility_functions'

  export default {
    name: 'case-review-view',
    mixins: [setPageHeightMixin, itemRefresher],
    data () {
      return {
        case_keys: ['stateCell', 'created', 'service.code', 'qualification.display', 'reviewer.name', 'responsible.profileTypeDisplay', 'modified', 'caseType.display'],
        caseSpecs: caseSpecs,
        kase: {},
        setAction: this.setCase,
        routes: [
          {
            route: { name: 'CaseReviewFindingsView' },
            text: 'FINDINGS'
          }
        ]
      }
    },
    props: {
      caseId: {
        required: true
      }
    },
    components: {
      TitleComponent,
      GridDetailsComponent,
      ReviewCustomerGridComponent,
      ReviewCustomerDocumentsComponent,
      ReviewCustomerExamComponent,
      ReviewServiceComponent,
      TabsRouterComponent
    },
    computed: {
      ...mapState({
        cases: state => state.caseModule.cases,
        reviewResults: state => state.caseReviewModule.reviewResults,
        authUser: state => state.authModule.authUser
      }),
      ...mapGetters([
        'getReviewResultFindings',
        'groupedReviewResults',
        'getReviewResultsForType'
      ]),
      customerReviewResults () {
        let customerGroups = ['customer_invoice', 'customer_profile']
        return this.groupedReviewResults(customerGroups)
      },
      reportingReviewResults () {
        return this.groupedReviewResults(['reporting'])
      },
      serviceReviewResults () {
        return this.groupedReviewResults('service_information')
      },
      serviceDocumentsReviewResults () {
        return this.getReviewResultsForType(this.groupedReviewResults('service_information'),
                                            [ReviewResult.FILE_TYPE, ReviewResult.MULTIPLE_FILES_TYPE])
      },
      serviceRadioReviewResults () {
        return this.groupedReviewResults('service_information').filter(reviewResult => {
          return !(reviewResult.getDataType() === ReviewResult.CHECKBOX_TYPE && reviewResult.isEmptyFormData()) &&
            !([ReviewResult.FILE_TYPE, ReviewResult.MULTIPLE_FILES_TYPE].includes(reviewResult.getDataType()))
        })
      }
    },
    methods: {
      ...mapActions([
        'setCase',
        'getReviewResults',
        'clearReviewResults'
      ]),
      ...mapMutations([
        'addPendingComment'
      ]),
      toggleFinding (reviewResult) {
        globalEventBus.$emit('toggleCollapseItem', reviewResult.id)
      },

      setReviewResults () {
        this.getReviewResults(this.caseId).then(() => {
          for (let result of this.getReviewResultFindings) {
            this.addPendingComment({ finding: result.finding, user: this.authUser })
          }
        }).catch((err) => {
          this.handleError(err)
        })
      },
      switchCaseKeys () {
        if (this.kase && this.kase.isOvertakingCase()) {
          this.case_keys.push('currentCa')
          this.case_keys.push('currentCertificateNumber')
        }
        if (this.kase && this.kase.isRenewalCase()) {
          this.case_keys.push('isCertificateReportedCell')
        }
      },
      _setCase () {
        return this.setOrRefreshItem('kase', this.cases, this.caseId, [])
      }
    },
    created () {
      let self = this
      this._setCase().then(function () {
        self.switchCaseKeys()
      })
      this.setReviewResults()

      globalEventBus.$on('reviewResultUpdated', this._setCase)
      globalEventBus.$on('reviewResultUpdated', this.setReviewResults)
    },
    beforeDestroy () {
      this.clearReviewResults()
    }
  }
</script>

<style lang="sass" scoped>
  .dynamic-height
    padding: 0
</style>
